import { type Product, getProducts } from '@/services/product'
import { getRecommendations } from '@/services/recommendation'
import Bugsnag from '@bugsnag/browser'
import { defineStore } from 'pinia'
import { AsyncOperationState } from './lib/AsyncOperationState'

const initialState = () => ({
  loadState: AsyncOperationState.IDLE,
  items: [] as Product[],
  reasons: {} as Record<string, string>,
  offerPercents: {} as Record<string, number>,
  introTexts: {} as Record<string, string>,
  stale: true,
})

type AlgorithmKey = 'buy_again' | 'new' | 'bestsellers' | 'featured'

export const useRecommendationStore = defineStore('recommendation', {
  state: () => ({
    bestsellers: initialState(),
    buy_again: initialState(),
    new: initialState(),
    featured: initialState(),
  }),
  actions: {
    async init(
      shopifyId: string,
      rechargeId: string | undefined,
      algorithm: AlgorithmKey,
      context: string[],
    ) {
      if (
        (this[algorithm].stale ||
          this[algorithm].loadState === AsyncOperationState.IDLE) &&
        shopifyId
      ) {
        await this.load(shopifyId, rechargeId, algorithm, context)
      }
    },
    async load(
      shopifyId: string,
      rechargeId: string | undefined,
      algorithm: AlgorithmKey,
      context: string[],
    ) {
      this[algorithm].loadState = AsyncOperationState.IN_PROGRESS
      try {
        const recommendations = await getRecommendations(
          shopifyId,
          rechargeId,
          algorithm,
          context,
        )
        const products = await getProducts(
          recommendations.map((r) => r.product_id),
        )
        if (products) {
          this[algorithm].items = products
          this[algorithm].reasons = Object.fromEntries(
            recommendations.flatMap((recommendation) =>
              recommendation.reason
                ? [[recommendation.product_id, recommendation.reason]]
                : [],
            ),
          )
          this[algorithm].offerPercents = Object.fromEntries(
            recommendations.flatMap((recommendation) =>
              recommendation.offer_percent
                ? [[recommendation.product_id, recommendation.offer_percent]]
                : [],
            ),
          )
          this[algorithm].introTexts = Object.fromEntries(
            recommendations.flatMap((recommendation) =>
              recommendation.intro_text
                ? [[recommendation.product_id, recommendation.intro_text]]
                : [],
            ),
          )
          this[algorithm].loadState = AsyncOperationState.DONE
          this[algorithm].stale = false
        } else {
          throw new Error('Could not load products')
        }
      } catch (e) {
        Bugsnag.notify(e as Error)
        this[algorithm].loadState = AsyncOperationState.ERRORED
      }
    },
  },
})
